/**
 * Subscription Plans Page
 *
 * Displays available subscription plans with their features and pricing.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  CreditCard,
  Users,
  FileText,
  Smartphone,
  Mail,
  CheckCircle,
  XCircle,
  Plus,
  Pencil,
  Tag,
} from 'lucide-react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import {
  DataTable,
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
} from '@/components/common';
import { subscriptionService } from '@/api/services';
import type { ListRequest, SubscriptionPlan, SubscriptionPlanTermGrid } from '@/api/types';
import { useToast } from '@/hooks/useToast';
import { useCan } from '@/hooks/usePermissions';
import { SubscriptionPlanFormModal } from './SubscriptionPlanFormModal';
import { PlanTermFormModal } from './PlanTermFormModal';
import { PlanTermsSubTable } from './PlanTermsSubTable';

// ============================================
// Feature Check Component
// ============================================

function FeatureCheck({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <CheckCircle className="h-4 w-4 text-success" />
  ) : (
    <XCircle className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
  );
}

// ============================================
// Page Component
// ============================================

export function SubscriptionPlansPage() {
  const toast = useToast();
  // Managing subscription plans/terms requires the plan-manage permission.
  const canManagePlans = useCan('orgPlan.plan.manage');

  // State
  const [data, setData] = useState<SubscriptionPlan[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [isTermOpen, setIsTermOpen] = useState(false);
  const [termPlan, setTermPlan] = useState<SubscriptionPlan | null>(null);
  const [editingTerm, setEditingTerm] = useState<SubscriptionPlanTermGrid | null>(null);

  // Fetch data from the paginated grid-list endpoint.
  const fetchPlans = useCallback(
    async (params: {
      pageIndex: number;
      pageSize: number;
      sorting: SortingState;
      search: string;
    }) => {
      setIsLoading(true);
      try {
        const term = params.search.trim();
        const request: ListRequest = {
          paging: {
            page: params.pageIndex + 1,
            size: params.pageSize,
          },
          filtering: term ? { name: term } : undefined,
          sorting:
            params.sorting.length > 0
              ? { [params.sorting[0].id]: params.sorting[0].desc ? 'desc' : 'asc' }
              : { orderNumber: 'asc' },
        };

        const result = await subscriptionService.getSubscriptionPlanGridList(request);

        const plans = result.list.map((item) => ({
          ...item.subscriptionPlan,
          planTerms: item.planTerms,
        })) as SubscriptionPlan[];

        setData(plans);
        setTotalRows(result.filteredRecords);
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error);
        toast.error('Failed to load subscription plans');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Debounce the search input (500ms) and reset to the first page on change.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Refetch on pagination / sorting / search change.
  useEffect(() => {
    fetchPlans({ ...pagination, sorting, search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, debouncedSearch]);

  // Refetch with the current pagination / sorting / search (used after mutations).
  const refetchPlans = useCallback(() => {
    fetchPlans({ ...pagination, sorting, search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPlans, pagination, sorting, debouncedSearch]);

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  // Column definitions
  const columns = useMemo<ColumnDef<SubscriptionPlan>[]>(
    () => {
      const baseColumns: ColumnDef<SubscriptionPlan>[] = [
      {
        accessorKey: 'name',
        header: 'Plan Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground dark:text-foreground-dark">
                {row.original.name}
              </p>
              <p className="text-xs text-foreground-muted dark:text-foreground-dark-muted">
                {row.original.code}
              </p>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Badge variant={status === 'ACTIVE' ? 'success' : 'default'} dot>
              {status}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'maxUsers',
        header: 'Max Users',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
            <span>{row.original.maxUsers.toLocaleString()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'maxEmployees',
        header: 'Max Employees',
        cell: ({ row }) => (
          <span>{row.original.maxEmployees.toLocaleString()}</span>
        ),
      },
      {
        accessorKey: 'maxContracts',
        header: 'Max Contracts',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <FileText className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
            <span>{row.original.maxContracts.toLocaleString()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'totalSmsCredits',
        header: 'SMS Credits',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Mail className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
            <span>{row.original.totalSmsCredits.toLocaleString()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'features',
        header: 'Features',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1" title="Mobile App">
              <Smartphone className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <FeatureCheck enabled={row.original.mobileAppFeatureEnabled} />
            </div>
            <div className="flex items-center gap-1" title="SMS Feature">
              <Mail className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <FeatureCheck enabled={row.original.smsFeatureEnabled} />
            </div>
            <div className="flex items-center gap-1" title="E-Invoice">
              <FileText className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <FeatureCheck enabled={row.original.eInvoiceEnabled} />
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'isCustomPlan',
        header: 'Type',
        cell: ({ row }) => (
          <Badge variant={row.original.isCustomPlan ? 'warning' : 'primary'}>
            {row.original.isCustomPlan ? 'Custom' : 'Standard'}
          </Badge>
        ),
      },
      ];

      // Create/update actions are restricted to ROLE_SADMIN only.
      if (!canManagePlans) {
        return baseColumns;
      }

      return [
        ...baseColumns,
        {
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Pencil className="h-4 w-4" />}
                onClick={() => {
                  setEditingPlan(row.original);
                  setIsFormOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Tag className="h-4 w-4" />}
                onClick={() => {
                  setTermPlan(row.original);
                  setEditingTerm(null);
                  setIsTermOpen(true);
                }}
              >
                Add Term
              </Button>
            </div>
          ),
        },
      ];
    },
    [canManagePlans]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Subscription Plans
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            View and manage available subscription plans
          </p>
        </div>
        {canManagePlans && (
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setEditingPlan(null);
              setIsFormOpen(true);
            }}
          >
            Create Subscription Plan
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              <CreditCard className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground dark:text-foreground-dark">
                {totalRows}
              </p>
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                Total Plans
              </p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-50 dark:bg-success/20">
              <CheckCircle className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground dark:text-foreground-dark">
                {data.filter((p) => p.status === 'ACTIVE').length}
              </p>
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                Active Plans
              </p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning-50 dark:bg-warning/20">
              <CreditCard className="h-6 w-6 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground dark:text-foreground-dark">
                {data.filter((p) => p.isCustomPlan).length}
              </p>
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                Custom Plans
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader
          title="Available Plans"
          description="All subscription plans available in the system"
        />
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            searchable
            searchPlaceholder="Search plans..."
            searchValue={search}
            onSearchChange={setSearch}
            paginated
            totalRows={totalRows}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            onPaginationChange={handlePaginationChange}
            sortingState={sorting}
            onSortingChange={handleSortingChange}
            emptyMessage="No subscription plans found"
            emptyDescription="There are no subscription plans configured yet."
            className="border-0"
            renderSubRow={(plan) => (
              <PlanTermsSubTable
                plan={plan}
                canManageTerms={canManagePlans}
                onEditTerm={(term) => {
                  setTermPlan(plan);
                  setEditingTerm(term);
                  setIsTermOpen(true);
                }}
              />
            )}
          />
        </CardBody>
      </Card>

      {/* Create / Edit Subscription Plan Modal */}
      <SubscriptionPlanFormModal
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        plan={editingPlan}
        onSuccess={refetchPlans}
      />

      {/* Add / Update Plan Term Modal */}
      <PlanTermFormModal
        open={isTermOpen}
        onOpenChange={setIsTermOpen}
        plan={termPlan}
        term={editingTerm}
        onSuccess={refetchPlans}
      />
    </div>
  );
}

export default SubscriptionPlansPage;
